import React, { useContext, useEffect } from 'react';
import Layout from '@/components/layout';
import PaymentOption from './_payment-options';
import { Helmet } from 'react-helmet';
import { ImportantInformation } from '@latitude/important-information-rebrand';
import { REBRAND_COLOR, PADDING } from '@latitude/core/utils/constants';
import PageData from './data/low-rate-data.json';
import { Accordion } from '@latitude/accordion-rebrand';
import { Link } from '@latitude/link';
import { AnalyticsLocationProvider } from 'latitude-analytics';
import { PageContext } from '@/context/PageContext'; // Ensure this import is correct
import LowRateMicrositeHeader from './_low-rate-header-rebrand';
import PromoBanner from '@/components/PromoBannerRebrand/PromoBannerRebrand';
import lowRatePageDataBackUp from './data/low-rate-data-backup.json';
import FeatureCardList from '@/components/FeatureCardListRebrand/FeatureCardListRebrand';
import SvgInline from '@latitude/svg-inline';
import { CARD_CODE, CARD_DATA } from '@/data/pages/credit-cards/card-offer';
import { AnalyticsContext } from 'latitude-analytics';
import LabRatesAndFeesColumn from '@/components/LabRatesAndFeesColumnRebrand';
import Button from '@latitude/button-rebrand';
import HeroV1 from '@/components/Hero/HeroV1/HeroV1';
import FeatureGridV1 from '@/components/FeatureGridV1/FeatureGridV1';
import { RewardsCarasoule } from '@/components/RewardsCarasoule/RewardsCarasoule';
import OfferSelector from '@/components/Offers/OfferSelector/OfferSelector';
import { getOfferSelectorDataFromContentful } from '@/components/Offers/OfferSelector/contentfulOfferSelector';
import StickyNavigation from '@/components/StickyNavigationRebrand/StickyNavigation';
import HowToApply from '@/components/HowToApplyRebrand/HowToApplyRebrand';
import Section from '@latitude/section-rebrand';
import Typography from '@latitude/typography';
import HeroV2 from '@/components/Hero/HeroV2/HeroV2';
import { getFeatureCardListRebrandDataFromContentful } from '@/components/FeatureCardListRebrand/contentfulFeatureCardListRebrand';
import FaqRebrand from '@/components/FaqRebrand/FaqRebrand';
import PromoBannerV2 from '@/components/lab-components/PromoBanner/PromoBannerV2';

const HERO_DATA = {
  slides: [
    {
      pillColor: 'none',
      pillIcon: <SvgInline name="icon-local-mall-rebrand" />,
      pillText: 'Low Rate Mastercard',
      pillTextColor: REBRAND_COLOR.WHITE,
      pillPadding: '8px 16px 8px 0',
      heading:
        'Pay off your debt faster with a 0% p.a. Balance Transfer for 24 months.*',
      subHeading:
        'When you apply, are approved, and transfer your balance by 30 April 2026. 3% balance transfer fee applies. New customers only.',
      imageUrl: '/images/credit-cards/hero-images/lr-bt-offer.webp',
      trackingId: 'lowrate',
      isMobileReversed: false,
      buttonComponent: (
        <Button
          className={`hero-cta`}
          variant="primary"
          size="lg"
          ctaText="Make a selection"
          ctaUrl="#limited-time-offer-selector"
          trackEventData={{
            category: 'text-link',
            location: `hero-low-rate-bt`,
            label: 'Make a selection'
          }}
          trackId="hero"
        />
      )
    }
  ],
  marginTop: "0px",
  marginTopMobile: "0px",
  background: REBRAND_COLOR.MID_BLUE_10
};

const PAGE_NAVIGATION = [
  {
    anchor: 'limited-time-offer-selector',
    label: 'Offers'
  },
  {
    anchor: 'benefits',
    label: 'Benefits'
  },
  {
    anchor: 'rewards',
    label: 'Rewards'
  },
  {
    anchor: 'rates',
    label: 'Rates & Fees'
  },
  {
    anchor: 'payment-options',
    label: 'Payment options'
  },
  {
    anchor: 'how-to-apply',
    label: 'How to apply'
  }
];

const FEATURE_GRID_DATA = {
  features: [
    {
      icon: <SvgInline name="icon-percent-rebrand" />,
      headingText: 'Let your budget breathe',
      paragraphText:
        'Enjoy a 13.99% p.a. purchase rate, so you can stretch your budget without stressing it.'
    },
    {
      icon: <SvgInline name="icon-confetti-rebrand" />,
      headingText: 'Earn on everyday spend',
      paragraphText: (
        <>
          Get 3% back in Latitude Rewards<sup>1</sup> when you set up recurring
          payments direct with participating utilities, telco, and streaming
          services.{' '}
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="https://assets.latitudefinancial.com/legals/terms-conditions/lowrate-rewards.pdf"
            trackEventData={{
              label: 'tc-applys',
              location: 'lowrate-rewards'
            }}
          >
            T&Cs apply.
          </Link>
        </>
      )
    },
    {
      icon: <SvgInline name="icon-users-rebrand" />,
      headingText: 'Team up for even more rewards',
      paragraphText: `Add an additional cardholder at no extra cost and become the ultimate rewards-earning team.`,
      learnMoreUrl:
        'https://latitudefs.zendesk.com/hc/en-au/articles/36591875170577-Additional-Cardholders'
    },
    {
      icon: <SvgInline name="icon-credit-card-rebrand" />,
      headingText: 'Your card details, whenever you need them',
      paragraphText:
        'Securely view your details anytime in the Latitude App to shop online, even when your physical card isn’t with you',
      learnMoreUrl: 'https://www.latitudefinancial.com.au/digital-card/'
    }
  ],
  moreFeatureTitle: 'View all benefits',

  moreFeatures: [
    {
      title: `Events without the dramas​`,
      content: (
        <>
          Protect your event tickets with the complimentary Event Ticket Protection insurance, up to $150 per ticket (capped at $1,200 per year)<sup>6</sup>, subject to T&Cs.{' '}
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/credit-cards/low-rate/insurance/"
            trackEventData={{
              label: 'low-rate-insurance',
              location: 'everyday-offers-and-discounts'
            }}
          >
            Learn More.
          </Link>
        </>
      )
    },  
    {
      title: (
        <>
          Up to 55 days interest free period<sup>2</sup>
        </>
      ),
      content: (
        <>
          Take advantage of up to 55 days interest free<sup>2</sup> on credit card general purchases.
        </>
      )
    },
    {
      title: `Everyday offers and discounts`,
      content: (
        <>
          Get cashback in Latitude Rewards when you shop the very latest
          Merchant Offers in our{' '}
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/mobile-app/"
            trackEventData={{
              label: 'latitude-app',
              location: 'everyday-offers-and-discounts'
            }}
          >
            Latitude App.
          </Link>
        </>
      )
    },
    {
      title: `Ready, Set, Tap`,
      content: (
        <>
          Introducing our{' '}
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/digital-wallets/apple-pay/"
            trackEventData={{
              label: 'instant-cardless-payment ',
              location: 'ready-set-tap'
            }}
          >
            instant cardless payment
          </Link>{' '}
          feature where you can apply, get approval, and start shopping from
          your phone in minutes.
        </>
      )
    },
    {
      title: `Top-rated app to complement your card`,
      content: (
        <>
          Stay on top of payments with the 4.8/5 rated Latitude App and never
          miss a due date.
        </>
      )
    }
  ]
};

const getFeatureGridDataFromContentful = featureSlider => {
  const richTextCards = featureSlider?.featureCards?.filter(
    card => card?.richText
  );

  if (!richTextCards?.length) return null;

  return {
    features: richTextCards.map(card => ({
      icon: card.icon ? <SvgInline name={card.icon} /> : undefined,
      headingText: card.title,
      richText: card.richText
    }))
  };
};

const OFFER_SELECTOR_DATA = {
  heading: 'Would you like to apply with the Balance Transfer offer?',
  offers: [
    {
      id: 'bt-24months',
      title: 'Yes, apply for the limited time offer',
      subtitle: '0% Balance Transfer for 25 months',
      content:
        '3% Balance Transfer fee and eligibility criteria apply. New customers only, must be approved by 31 January 2027. Balance Transfer must be submitted by 31 March 2027. T&Cs, annual card fee <b>(normally $69)</b> and other charges apply. Interest rate reverts to the cash advance rate after the promotional period, currently 29.99% (subject to change). A Balance Transfer cannot be made from a Latitude issued credit card.',
      productId: '8F5UH373',
      ctaText: 'Apply now with a balance transfer',
      ctaURL: 'https://cards.latitudefinancial.com/low-rate?source_code=IDAAA'
    },
    {
      id: 'no-bt-offer',
      title: 'No, continue without the Balance Transfer offer',
      subtitle: 'Enjoy the benefits of using a Latitude Low Rate Mastercard',
      content:
        'A low rate and rewards credit card rolled into one. Spoil yourself, and get more from your budget, with the Latitude Low Rate Mastercard.',
      ctaText: 'Apply now',
      ctaURL: 'https://cards.latitudefinancial.com/low-rate?source_code=IDAAA'
    }
  ]
};

const HOW_TO_APPLY_DATA = {
  id: "how-to-apply",
  howToApply: {
    sectionTitle: 'How To apply',
    items: [
      {
        title: 'Apply online',
        description: 'You can check your eligibility and apply in 10 minutes.'
      },
      {
        title: 'Wait a minute',
        description:
          '60 seconds is all it takes for us to respond to your application.'
      },
      {
        title: 'Ready, set, shop',
        description:
          'Simply activate your card when it arrives to start shopping.'
      }
    ]
  },
  eligibility: {
    sectionTitle: 'Eligibility',
    items: [
      {
        title: 'You must:',
        list: [
          'Be 18 years or over.',
          'A resident of Australia or a temporary resident who legally has the right to work in Australia (and intend to stay) for at least the next 12 months.'
        ]
      },
      {
        title: 'Able to supply:',
        list: [
          "A copy of your driver's licence, passport or proof of age.",
          'Details of your income, assets and debts.',
          "Employer's name and contact details."
        ]
      }
    ]
  }
};

const cardData = CARD_DATA.filter(card => card.code !== CARD_CODE.LR);
const LowRateCardPage = props => {
  /** Contentful Data */
  const contentfulPageData = useContext(PageContext);
  const state = contentfulPageData?.updatedBody || lowRatePageDataBackUp;
  const featureGridData =
    getFeatureGridDataFromContentful(state?.featureSliderData?.[0]) ||
    FEATURE_GRID_DATA;
  const offerSelectorData =
    getOfferSelectorDataFromContentful(state?.offerSelectorData?.[0]) ||
    OFFER_SELECTOR_DATA;
  const featureCardListData = getFeatureCardListRebrandDataFromContentful(
    state?.featureCardListRebrandData?.[0]
  ) || { cardList: cardData };
  /** - END - */

  const [analytics] = useContext(AnalyticsContext);
  useEffect(() => {
    const sourceCode = localStorage.getItem('source_code') || 'IDAAA';
    // adding pageData to analytics
    analytics.pageData = {
      page_pageTitle: 'low-rate',
      page_pageType: 'product-details-page',
      page_siteSection: 'cards',
      page_siteSubsection: 'low-rate',
      product_productid: ['CCAULRM'],
      application_sourcecode: sourceCode || 'IDAAA'
    };
  }, []);

  useEffect(() => {
    if (window.location.hash=== '#limited-time-offer-selector') {
      const element = document.getElementById('limited-time-offer-selector');
      if (element) {
        window.scrollTo(0, 0);
        setTimeout(() => {
        window.scrollTo({
          top: element.offsetTop - 146,
          behavior: 'smooth'
        });
      }, 400);
      }
    }
  }, []);

  return (
    <Layout location={props.location}>
      <main>
        <Helmet>
          <link
            rel="canonical"
            href={
              props.canonical ||
              'https://www.latitudefinancial.com.au/credit-cards/low-rate/'
            }
          />
          <title>
            {props.title ||
              'Latitude Low Rate Mastercard | Rewards Credit Card'}
          </title>
          <meta
            name="description"
            content="Enjoy a low-rate credit card with Latitude rewards back on select utility bills. Choose between balance transfer or bonus rewards offer. Apply now."
          />
        </Helmet>
        <LowRateMicrositeHeader />

        <div
          css={`
            img[src*='lrmc-awards-overlay.webp']{
              @media (min-width: 768px) {
                width: 250px !important;
              }
              @media (max-width: 767px) {
                display: none;
              }
            }
          `}
        >
          <HeroV2
            {...(state?.heroBannerData?.[0] || PageData?.heroBannerData?.[0])}
          />
        </div>
        <OfferSelector
          trackingId="lowrate"
          id="limited-time-offer-selector"
          {...offerSelectorData}
        />

        <StickyNavigation
          items={PAGE_NAVIGATION}
          enableAnchorUpdate={true}
          trackId="sticky-get-started"
          stickyOffset={146}
        />

        <FeatureGridV1
          id="benefits"
          trackingId="lowrate"
          {...featureGridData}
        />

        <PromoBannerV2 {...state?.promoBannerData?.[0]}/>

        <RewardsCarasoule
          id="rewards"
          title="Experience more with Latitude Rewards"
          subtitle="Each Latitude Reward is worth $1 towards dining and cinema experiences, eGift Cards, and more &mdash; no points conversion needed."
          rewards={[
            {
              imageUrl: '/images/credit-cards/low-rate/rewards-1.webp',
              title: 'Earn on everyday spend',
              description: (
                <span>
                  Get 3% back in Latitude Rewards<sup>1</sup> when you set up
                  recurring payments direct with participating utilities, telco,
                  and streaming services.{' '}
                  <Link
                    noStyle
                    style={{ textDecoration: 'underline' }}
                    href="https://assets.latitudefinancial.com/legals/terms-conditions/lowrate-rewards.pdf"
                    trackEventData={{
                      location:
                        'experience-more-with-latitude-rewards' ||
                        'rewards-carousel'
                    }}
                  >
                    T&Cs apply
                  </Link>
                  .
                </span>
              )
            },
            {
              imageUrl: '/images/credit-cards/low-rate/rewards-2.webp',
              title: 'Browse tons of rewards',
              description:
                (<>Use your Latitude Rewards<sup>1</sup> for dining and cinema experiences, eGift Cards, and more. Enjoy your rewards your way.</>)
            },
            {
              imageUrl: '/images/credit-cards/low-rate/rewards-3.webp',
              title: 'Redeem in seconds',
              description: (
                <span>
                  Redeem your rewards in seconds. Just use the Latitude App or{' '}
                  <Link
                    noStyle
                    style={{ textDecoration: 'underline' }}
                    href="https://servicecentre.latitudefinancial.com.au/login?migration=true"
                  >
                    log in
                  </Link>{' '}
                  to the Latitude Service Centre.
                </span>
              )
            }
          ]}
          rewardsCta={
            <Button
              className={`rewards-carousel-cta`}
              variant="secondary-blue"
              size="lg"
              ctaText="   Learn More"
              ctaUrl="/credit-cards/low-rate/latitude-rewards"
              trackEventData={{
                category: 'text-link',
                location: 'experience-more-with-latitude-rewards',
                label: 'learn-more'
              }}
              trackId="rewards-carousel"
            />
          }
        />

        <LabRatesAndFeesColumn isRate={true} {...state.ratesAndFeesData[0]} />

        {/* <PromoBanner {...state?.promoBannerData?.[0]} /> */}

        {/* {state?.promoBannerData?.[1] && (
          <PromoBanner {...state.promoBannerData?.[1]} />
        )} */}

        <PaymentOption />

        {state?.promoBannerData?.[1] && <PromoBanner {...state?.promoBannerData?.[1]}/>}

        <HowToApply data={HOW_TO_APPLY_DATA} />

        <FeatureCardList {...featureCardListData} />

        <FaqRebrand
          title="Frequently Asked Questions"
          accordions={PageData.Faq}
          ctaText={'See all FAQ'}
          ctaUrl={'/credit-cards/low-rate/faqs/'}
        />
        <ImportantInformation
          id="important-Info"
          css={`overflow-x: hidden; .row {
              @media (min-width:1600px) {
                padding-left: ${PADDING.P32};
                padding-right: ${PADDING.P32};
              }
            }`}
          data={require('../../../data/pages/credit-cards/latitude-low-rate-mastercard.json')}
          {...{
            ...state?.importantInfoData?.[0],
            sectionOneColummClasses: 'col-12 col-lg-6',
            sectionTwoColummClasses: 'col-12 col-lg-6'
          }}
        />
      </main>
    </Layout>
  );
};

export default LowRateCardPage;